import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  Building2,
  Loader2,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  Pencil,
  Trash2,
  Briefcase,
  Hammer,
  Key,
  Globe,
  Activity
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessTypes = [
  { value: 'web_marketing', label: 'Web & Marketing Agency', icon: Briefcase, description: 'Web design, SEO, digital marketing services', color: 'bg-blue-50 text-blue-700' },
  { value: 'construction', label: 'Construction Contractor', icon: Hammer, description: 'Construction, renovation, contracting services', color: 'bg-amber-50 text-amber-700' },
  { value: 'dme_medical', label: 'DME Medical', icon: Activity, description: 'Durable Medical Equipment billing and order management', color: 'bg-red-50 text-red-700' }
];

const Tenants = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',  // Contact name
    company_name: '',
    contact_email: '',
    password: '',  // Password for tenant admin
    phone: '',
    address: '',
    business_type: 'web_marketing',
    custom_domain: '',
    portal_slug: ''
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/tenants`, getAuthHeaders());
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate email and password
    if (!formData.contact_email || !formData.password) {
      toast.error('Email and password are required for tenant admin account');
      return;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/tenants`, formData, getAuthHeaders());
      toast.success('Tenant created with admin account');
      setIsCreateOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/tenants/${selectedTenant.id}`, formData, getAuthHeaders());
      toast.success('Tenant updated successfully');
      setIsEditOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    }
  };

  const handleDelete = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant? This will affect all associated data.')) return;
    
    try {
      await axios.delete(`${API_URL}/tenants/${tenantId}`, getAuthHeaders());
      toast.success('Tenant deleted successfully');
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleSendCredentials = async (tenantId, tenantName, tenantEmail) => {
    if (!window.confirm(`Send login credentials to ${tenantName} (${tenantEmail})?\n\nThis will reset their admin password and send a new one via email.`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/tenants/${tenantId}/send-credentials`, {}, getAuthHeaders());
      toast.success(response.data.message || 'Credentials sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    }
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      name: tenant.name,
      company_name: tenant.company_name || '',
      contact_email: tenant.contact_email || '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      business_type: tenant.business_type || 'web_marketing',
      custom_domain: tenant.custom_domain || '',
      portal_slug: tenant.portal_slug || ''
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', company_name: '', contact_email: '', password: '', phone: '', address: '', business_type: 'web_marketing', custom_domain: '', portal_slug: '' });
    setSelectedTenant(null);
  };

  const filteredTenants = tenants.filter(tenant => 
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tenant.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Tenants
          </h1>
          <p className="text-slate-500 mt-1">Manage agencies and organizations in the platform</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-tenant-btn">
              <Plus size={18} className="mr-2" />
              New Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Create Tenant</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Contact Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  required
                  data-testid="tenant-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="e.g., Lockheart Media LLC"
                  data-testid="tenant-company-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Business Type *</Label>
                <Select
                  value={formData.business_type}
                  onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                >
                  <SelectTrigger data-testid="tenant-business-type-select">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon size={16} className="text-slate-500" />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {businessTypes.find(t => t.value === formData.business_type)?.description}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Contact Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@agency.com"
                  data-testid="tenant-email-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Password <span className="text-red-500">*</span></Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Admin login password"
                  data-testid="tenant-password-input"
                  required
                />
                <p className="text-xs text-slate-500">This will be used for the tenant admin login</p>
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                  data-testid="tenant-phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, City, State"
                  data-testid="tenant-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Globe size={14} className="text-slate-500" />
                  Portal URL Slug
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 whitespace-nowrap">myclientpm.com/</span>
                  <Input
                    value={formData.portal_slug}
                    onChange={(e) => setFormData({ ...formData, portal_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="nmnetwork"
                    data-testid="tenant-slug-input"
                  />
                </div>
                {formData.portal_slug && (
                  <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                    <Globe size={12} />
                    Portal: https://myclientpm.com/{formData.portal_slug}
                  </p>
                )}
                <p className="text-xs text-slate-400">Lowercase letters, numbers and hyphens only</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Globe size={14} className="text-slate-500" />
                  Custom Portal Subdomain
                </Label>
                <Input
                  value={formData.custom_domain}
                  onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value.toLowerCase().trim() })}
                  placeholder="e.g. lockheart.myclientpm.com"
                  data-testid="tenant-domain-input"
                />
                <p className="text-xs text-slate-500">
                  Enter a subdomain under your wildcard domain (e.g. <strong>lockheart.myclientpm.com</strong>).
                  Make sure your DNS has a wildcard CNAME: <code>* → myclientpm.com</code>
                </p>
                {formData.custom_domain && (
                  <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                    <Globe size={12} />
                    Portal URL: https://{formData.custom_domain}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-tenant-btn">
                  Create Tenant
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-tenants-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tenants Grid */}
      {filteredTenants.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No tenants found</h3>
            <p className="text-slate-500 mt-1">Create your first tenant to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTenants.map((tenant) => (
            <Card 
              key={tenant.id} 
              className="border-slate-200 shadow-sm card-hover cursor-pointer"
              onClick={() => navigate(`/tenants/${tenant.id}`)}
              data-testid={`tenant-card-${tenant.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {tenant.logo ? (
                      <img 
                        src={`data:${tenant.logo_content_type || 'image/png'};base64,${tenant.logo}`}
                        alt={tenant.name}
                        className="w-10 h-10 rounded-lg object-contain border border-slate-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
                        <Building2 size={20} className="text-white" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg font-semibold text-slate-900">
                        {tenant.company_name || tenant.name}
                      </CardTitle>
                      {tenant.company_name && (
                        <p className="text-sm text-slate-600">{tenant.name}</p>
                      )}
                      <p className="text-xs text-slate-400">
                        Created {format(new Date(tenant.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`tenant-menu-${tenant.id}`}>
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModal(tenant)}>
                        <Pencil size={14} className="mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleSendCredentials(tenant.id, tenant.company_name || tenant.name, tenant.contact_email)}
                        data-testid={`send-credentials-${tenant.id}`}
                      >
                        <Key size={14} className="mr-2" />
                        Send Credentials
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(tenant.id)}
                        className="text-red-600"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {tenant.contact_email && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Mail size={14} className="text-slate-400" />
                      <a href={`mailto:${tenant.contact_email}`} className="hover:text-blue-600 truncate">
                        {tenant.contact_email}
                      </a>
                    </p>
                  )}
                  {tenant.phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <Phone size={14} className="text-slate-400" />
                      {tenant.phone}
                    </p>
                  )}
                  {tenant.address && (
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <MapPin size={14} className="text-slate-400" />
                      <span className="truncate">{tenant.address}</span>
                    </p>
                  )}
                  {/* Business Type Badge */}
                  {tenant.business_type && (
                    <div className="pt-2 mt-2 border-t border-slate-100">
                      {(() => {
                        const bt = businessTypes.find(t => t.value === tenant.business_type) || businessTypes[0];
                        const BtIcon = bt.icon;
                        return (
                          <>
                            <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${bt.color}`}>
                              <BtIcon size={12} />
                              {bt.label}
                            </span>
                            {tenant.custom_domain && (
                              <a
                                href={`https://${tenant.custom_domain}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                                title={`Portal: https://${tenant.custom_domain}`}
                              >
                                <Globe size={11} />
                                {tenant.custom_domain}
                              </a>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Tenant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Contact Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="edit-tenant-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                data-testid="edit-tenant-company-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Business Type *</Label>
              <Select
                value={formData.business_type}
                onValueChange={(value) => setFormData({ ...formData, business_type: value })}
              >
                <SelectTrigger data-testid="edit-tenant-business-type-select">
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon size={16} className="text-slate-500" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                data-testid="edit-tenant-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="edit-tenant-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="edit-tenant-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe size={14} className="text-slate-500" />
                Portal URL Slug
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 whitespace-nowrap">myclientpm.com/</span>
                <Input
                  value={formData.portal_slug}
                  onChange={(e) => setFormData({ ...formData, portal_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="nmnetwork"
                  data-testid="edit-tenant-slug-input"
                />
              </div>
              {formData.portal_slug && (
                <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                  <Globe size={12} />
                  Portal: https://myclientpm.com/{formData.portal_slug}
                </p>
              )}
              <p className="text-xs text-slate-400">Lowercase letters, numbers and hyphens only</p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe size={14} className="text-slate-500" />
                Custom Portal Subdomain
              </Label>
              <Input
                value={formData.custom_domain}
                onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value.toLowerCase().trim() })}
                placeholder="e.g. lockheart.myclientpm.com"
                data-testid="edit-tenant-domain-input"
              />
              <p className="text-xs text-slate-500">
                Enter a subdomain under your wildcard domain (e.g. <strong>lockheart.myclientpm.com</strong>).
                Make sure your DNS has a wildcard CNAME: <code>* → myclientpm.com</code>
              </p>
              {formData.custom_domain && (
                <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                  <Globe size={12} />
                  Portal URL: https://{formData.custom_domain}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="update-tenant-btn">
                Update Tenant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tenants;
